import * as React from 'react';

import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { RouteNames } from 'pages/teach-course/constants';

import type Item from 'bundles/author-course/models/Item';
import type SessionItem from 'bundles/author-sessions/models/SessionItem';
import GradedItemTypes from 'bundles/authoring/common/items/constants/GradedItemTypes';
import UngradedItemTypes from 'bundles/authoring/common/items/constants/UngradedItemTypes';
import type { ItemGrade } from 'bundles/authoring/common/types/gradebookV2';
import type { ProctorConfig } from 'bundles/authoring/common/types/proctorConfig';
import useHeartbeat from 'bundles/grading-common/components/heartbeat/useHeartbeat';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import SGAGrading from 'bundles/grading/components/staff-graded-assignments/SGAGrading';
import type { ItemType } from 'bundles/item/types/ItemType';

type Props = {
  itemType: ItemType;
  courseId: string;
  courseBranchId: string;
  currentItem: Item;
  sessionItem: SessionItem;
  gradebookGrades: { [userId: string]: ItemGrade };
  isAnonymized: boolean;
  proctorConfigDraft: ProctorConfig;
  // for private sessions only
  groupId?: string;
};

// Maps an item type to the correct item submission wrapper
const GradingItemComponentMapping = {
  [GradedItemTypes.Quiz]: null,
  [GradedItemTypes.Programming]: null,
  [UngradedItemTypes.UngradedProgramming]: null,
  [GradedItemTypes.StaffGraded]: SGAGrading,
  [GradedItemTypes.Peer]: null,
  [UngradedItemTypes.UngradedPeer]: null,
  [GradedItemTypes.GradedDiscussionPrompt]: null,
  [GradedItemTypes.TeammateReview]: null,
  [GradedItemTypes.GradedLTI]: null,
  [GradedItemTypes.WiseFlow]: null,
};

/**
 * "Quick Grader" view for gradable item submissions.
 * Renders whole screen overlay with top bar, left/middle/right panels,
 * list of submitters and submission and grading details for a selected
 * submission.
 * The original idea was to implement it for different gradable items,
 * with "SGA (Staff Graded Assignment)" as a pilot. As priorities changed,
 * this interface became specific to SGA only.
 * @param oldSubmissionGradingProps
 * @returns React.JSX.Element
 */
const SubmissionGrading = (oldSubmissionGradingProps: Props) => {
  const routerParams = useParams();
  const graderId = user.get().id;

  const { courseId, courseBranchId, sessionItem } = oldSubmissionGradingProps;

  /**
   * Begin collecting heartbeat data
   * will send eventing data every 10 sec while quick-grader is running
   *
   * Setting submitterId as a skipKey here since for quick-grader we want to track
   * in the scope of grading in general, not just per submission-base. In the old UI
   * the submitterId would define a "chunk" of metric as the heartbeat would check
   * for changes in eventing data to determine whether to start a new "chunk" or not.
   * So now we want to ensure it isn't used to restart a new "chunk" (new heartbeat
   * throttle) in quick-grader when switching submitters
   */
  useHeartbeat({
    page: 'sga_submission_grading',
    routeNames: [RouteNames.ASSIGNMENT_QUICK_GRADER],
    version: 'v2',
    eventData: {
      courseId,
      courseBranchId,
      sessionId: sessionItem?.sessionId,
      itemId: routerParams.itemId,
      submitterId: routerParams.id,
      // TODO: contextId evaluates to authoringBranchId in Public sessions, and
      // groupId in Private. Revisit - what exactly we want to track here?
      groupId: routerParams.contextId,
      graderId,
    },
  });
  const SpecificItemGradingWrapper = GradingItemComponentMapping[oldSubmissionGradingProps.itemType];
  if (SpecificItemGradingWrapper) {
    return <SpecificItemGradingWrapper {...oldSubmissionGradingProps} />;
  } else {
    return null;
  }
};

export default SubmissionGrading;
